import React from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Edit as EditIcon, People as PeopleIcon, Close as CloseIcon } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { get, patch } from '../../utils/api';

export default function KotilingaInventorySlotsManager({ onInventoryMutated }) {
  const parsePositiveId = React.useCallback((value) => {
    if (value == null) return '';
    if (typeof value === 'number' && Number.isFinite(value) && value > 0) return Math.trunc(value);
    const raw = String(value).trim();
    if (!raw) return '';
    const normalized = raw.replace(/,/g, '');
    const numeric = Number(normalized);
    if (Number.isFinite(numeric) && numeric > 0) return Math.trunc(numeric);
    const match = normalized.match(/\d+/);
    if (match) {
      const extracted = Number(match[0]);
      if (Number.isFinite(extracted) && extracted > 0) return Math.trunc(extracted);
    }
    return '';
  }, []);

  const [rows, setRows] = React.useState([]);
  const [categories, setCategories] = React.useState([]);
  const [statuses, setStatuses] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');

  const [categoryFilter, setCategoryFilter] = React.useState('all');
  const [statusFilter, setStatusFilter] = React.useState('all');
  const [searchQ, setSearchQ] = React.useState('');
  const [debouncedQ, setDebouncedQ] = React.useState('');
  const [page, setPage] = React.useState(1);
  const [perPage] = React.useState(25);
  const [total, setTotal] = React.useState(0);
  const [totalPages, setTotalPages] = React.useState(0);

  const [editOpen, setEditOpen] = React.useState(false);
  const [editingItem, setEditingItem] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState('');
  const [saveSuccess, setSaveSuccess] = React.useState('');

  const [editProductInput, setEditProductInput] = React.useState('');
  const [editProductOptions, setEditProductOptions] = React.useState([]);
  const [editProductLoading, setEditProductLoading] = React.useState(false);
  const [selectedEditProduct, setSelectedEditProduct] = React.useState(null);
  const [editVariationOptions, setEditVariationOptions] = React.useState([]);
  const [editVariationLoading, setEditVariationLoading] = React.useState(false);

  const [buyersLoading, setBuyersLoading] = React.useState(false);
  const [buyersError, setBuyersError] = React.useState('');
  const [buyers, setBuyers] = React.useState([]);
  const [productDetailsLoading, setProductDetailsLoading] = React.useState(false);
  const [productDetailsError, setProductDetailsError] = React.useState('');
  const [productDetails, setProductDetails] = React.useState(null);

  const loadRows = React.useCallback(async () => {
    const params = { page: String(page), per_page: String(perPage) };
    if (categoryFilter !== 'all') params.category_id = categoryFilter;
    if (statusFilter !== 'all') params.status = statusFilter;
    if (debouncedQ) params.q = debouncedQ;
    const res = await get('/ldb/kotilinga-inventory/items', params);
    const list = Array.isArray(res?.data) ? res.data : [];
    setRows(list);
    setTotal(Number(res?.meta?.total || 0));
    setTotalPages(Number(res?.meta?.total_pages || 0));
    return list;
  }, [page, perPage, categoryFilter, statusFilter, debouncedQ]);

  const productLabel = React.useCallback((p) => {
    const name = String(p?.name || '').trim();
    const sku = String(p?.sku || '').trim();
    if (name && sku) return `${name} · ${sku}`;
    return name || sku || '';
  }, []);

  const variationLabel = React.useCallback((v) => {
    const display = String(v?.display_name || v?.name || '').trim();
    const sku = String(v?.sku || '').trim();
    return display || sku || 'Variation';
  }, []);

  const loadBuyers = React.useCallback(async (productId, variationId) => {
    const pid = Number(productId || 0);
    const vid = Number(variationId || 0);
    if (!pid) {
      setBuyers([]);
      setBuyersError('');
      return;
    }
    setBuyersLoading(true);
    setBuyersError('');
    try {
      const res = await get('/ldb/kotilinga-order-inventory/sponsors', {
        product_id: String(pid),
        variation_id: String(vid > 0 ? vid : 0),
        page: 1,
        page_size: 500,
      });
      setBuyers(Array.isArray(res?.data) ? res.data : []);
    } catch (e) {
      setBuyers([]);
      setBuyersError(e?.message || 'Failed to load buyers from orders');
    } finally {
      setBuyersLoading(false);
    }
  }, []);

  const loadProductDetails = React.useCallback(async (productId, variationId, sourceRow = {}) => {
    const pid = Number(productId || 0);
    if (!pid) {
      setProductDetails(null);
      setProductDetailsError('');
      return;
    }
    setProductDetailsLoading(true);
    setProductDetailsError('');
    try {
      const res = await get(`/ldb/products/${pid}`);
      const product = res?.product || {};
      const variations = Array.isArray(res?.variations) ? res.variations : [];
      const vid = Number(variationId || 0);
      const variation = vid > 0 ? variations.find((v) => Number(v.id) === vid) || null : null;
      setProductDetails({
        product,
        variation,
        product_name: product.name || sourceRow.linked_product_name || sourceRow.product_name || `Product #${pid}`,
        category_names: sourceRow.linked_product_category_names || '',
        variation_name: variation
          ? variationLabel(variation)
          : (sourceRow.linked_variation_name || (vid > 0 ? `Variation #${vid}` : '—')),
      });
    } catch (e) {
      setProductDetails(null);
      setProductDetailsError(e?.message || 'Failed to load product details');
    } finally {
      setProductDetailsLoading(false);
    }
  }, [variationLabel]);

  const currency = React.useCallback((value) => (
    Number(value || 0).toLocaleString(undefined, { style: 'currency', currency: 'USD' })
  ), []);

  React.useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedQ(searchQ.trim().length >= 2 ? searchQ.trim() : '');
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [searchQ]);

  React.useEffect(() => {
    (async () => {
      try {
        const [catsRes, statusesRes] = await Promise.all([
          get('/ldb/kotilinga-inventory/categories'),
          get('/ldb/kotilinga-inventory/statuses'),
        ]);
        setCategories(Array.isArray(catsRes?.data) ? catsRes.data : []);
        setStatuses(Array.isArray(statusesRes?.data) ? statusesRes.data : []);
      } catch {
        setCategories([]);
        setStatuses([]);
      }
    })();
  }, []);

  React.useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setError('');
        await loadRows();
      } catch (e) {
        setError(e?.message || 'Failed to load inventory');
      } finally {
        setLoading(false);
      }
    })();
  }, [loadRows]);

  const loadEditVariations = React.useCallback(async (productId) => {
    if (!productId) {
      setEditVariationOptions([]);
      return;
    }
    setEditVariationLoading(true);
    try {
      const res = await get(`/ldb/products/${productId}`);
      setEditVariationOptions(Array.isArray(res?.variations) ? res.variations : []);
    } catch {
      setEditVariationOptions([]);
    } finally {
      setEditVariationLoading(false);
    }
  }, []);

  React.useEffect(() => {
    if (!editOpen) {
      setEditProductOptions([]);
      return;
    }
    const q = editProductInput.trim();
    if (q.length < 2) {
      setEditProductOptions(selectedEditProduct ? [selectedEditProduct] : []);
      return;
    }
    let cancelled = false;
    setEditProductLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await get('/ldb/products', { q, page: 1, per_page: 20 });
        const list = Array.isArray(res?.data) ? res.data : [];
        const merged = selectedEditProduct && !list.some((x) => Number(x.id) === Number(selectedEditProduct.id))
          ? [selectedEditProduct, ...list]
          : list;
        if (!cancelled) setEditProductOptions(merged);
      } catch {
        if (!cancelled) setEditProductOptions(selectedEditProduct ? [selectedEditProduct] : []);
      } finally {
        if (!cancelled) setEditProductLoading(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [editOpen, editProductInput, selectedEditProduct]);

  React.useEffect(() => {
    if (!editOpen) return;
    const pid = parsePositiveId(editingItem?.product_id);
    if (!pid || selectedEditProduct) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await get(`/ldb/products/${pid}`);
        const p = res?.product || {};
        const hydrated = {
          id: Number(p.id || pid),
          name: String(p.name || `Product #${pid}`),
          sku: String(p.sku || ''),
        };
        if (!cancelled) {
          setSelectedEditProduct(hydrated);
          setEditProductInput(hydrated.name);
          setEditProductOptions((prev) => {
            const list = Array.isArray(prev) ? prev : [];
            return list.some((x) => Number(x?.id) === Number(hydrated.id))
              ? list
              : [hydrated, ...list];
          });
        }
      } catch {
        // leave as-is if product hydrate fails
      }
    })();
    return () => { cancelled = true; };
  }, [editOpen, editingItem?.product_id, selectedEditProduct, parsePositiveId]);

  const openManageDialog = async (row) => {
    let sourceRow = row || {};
    const inventoryId = parsePositiveId(row?.inventory_id);
    if (inventoryId) {
      try {
        const res = await get(`/ldb/kotilinga-inventory/items/${inventoryId}`);
        if (res?.data) sourceRow = res.data;
      } catch {
        // fall back to grid row if detail fetch fails
      }
    }

    let productId = parsePositiveId(sourceRow?.product_id);
    const variationId = parsePositiveId(sourceRow?.variation_id);

    let selected = productId
      ? { id: productId, name: sourceRow?.linked_product_name || sourceRow?.product_name || `Product #${productId}`, sku: '' }
      : null;

    setEditingItem({
      inventory_id: sourceRow.inventory_id,
      product_name: sourceRow.product_name ?? '',
      product_id: productId,
      variation_id: variationId || '',
      product_no: sourceRow.product_no ?? '',
      quantity: sourceRow.quantity ?? 0,
      category_id: sourceRow.category_id ?? '',
      status: sourceRow.status ?? '',
    });
    setSelectedEditProduct(selected);
    setEditProductInput(selected?.name || '');
    setEditProductOptions(selected ? [selected] : []);
    void loadEditVariations(productId || null);
    void loadProductDetails(productId, variationId, sourceRow);
    void loadBuyers(productId, variationId);
    setSaveError('');
    setSaveSuccess('');
    setEditOpen(true);
  };

  const handleSave = async () => {
    if (!editingItem) return;
    try {
      setSaving(true);
      setSaveError('');
      await patch(`/ldb/kotilinga-inventory/items/${editingItem.inventory_id}`, {
        product_name: editingItem.product_name || null,
        product_id: editingItem.product_id === '' ? null : Number(editingItem.product_id),
        product_no: editingItem.product_no || null,
        quantity: editingItem.quantity === '' ? null : Number(editingItem.quantity),
        variation_id: editingItem.variation_id === '' ? 0 : Number(editingItem.variation_id),
        category_id: editingItem.category_id === '' ? null : Number(editingItem.category_id),
        status: editingItem.status === '' ? null : Number(editingItem.status),
      });
      const latest = await loadRows();
      const refreshed = latest.find((x) => x.inventory_id === editingItem.inventory_id);
      if (refreshed) {
        const pid = Number(refreshed.product_id || 0);
        const vid = Number(refreshed.variation_id || 0);
        setEditingItem((prev) => (prev ? { ...prev, product_id: pid || '', variation_id: vid || '' } : prev));
        await loadBuyers(pid, vid);
      } else {
        await loadBuyers(editingItem.product_id, editingItem.variation_id);
      }
      setSaveSuccess('Item saved. Buyers refreshed from orders.');
      if (typeof onInventoryMutated === 'function') onInventoryMutated();
    } catch (e) {
      setSaveError(e?.message || 'Failed to save item');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Manage slots &amp; buyer mapping</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Buyers are dynamic from orders table by linked product/variation. No manual sponsor assignment here.
      </Typography>

      <Paper elevation={0} sx={{ p: 2.5, mb: 3, border: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '2fr 1fr 1fr auto' } }}>
          <TextField
            size="small"
            label="Search"
            value={searchQ}
            onChange={(e) => setSearchQ(e.target.value)}
            placeholder="Product, variation, sponsor, category, donor..."
          />
          <FormControl fullWidth size="small">
            <InputLabel>Category</InputLabel>
            <Select value={categoryFilter} label="Category" onChange={(e) => { setCategoryFilter(e.target.value); setPage(1); }}>
              <MenuItem value="all">All</MenuItem>
              {categories.map((c) => <MenuItem key={c.category_id} value={String(c.category_id)}>{c.category_name}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small">
            <InputLabel>Status</InputLabel>
            <Select value={statusFilter} label="Status" onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
              <MenuItem value="all">All</MenuItem>
              {statuses.map((s) => <MenuItem key={s.status_id} value={String(s.status_id)}>{s.status_name}</MenuItem>)}
            </Select>
          </FormControl>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {(categoryFilter !== 'all' || statusFilter !== 'all' || debouncedQ) && (
              <Chip
                size="small"
                label="Clear"
                onClick={() => {
                  setCategoryFilter('all');
                  setStatusFilter('all');
                  setSearchQ('');
                  setDebouncedQ('');
                  setPage(1);
                }}
              />
            )}
            <Typography variant="body2" color="text.secondary">Total: {total}</Typography>
          </Box>
        </Box>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ px: 2, py: 1.25, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">Page {page} of {totalPages || 1} — {rows.length} of {total} items</Typography>
          <Pagination count={totalPages || 1} page={page} onChange={(_, v) => setPage(v)} size="small" showFirstButton showLastButton />
        </Box>
        {loading ? (
          <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Box sx={{ minWidth: 1120, display: 'grid', gridTemplateColumns: 'minmax(100px,0.85fr) minmax(130px,1fr) minmax(140px,1fr) minmax(150px,1.1fr) minmax(150px,1.1fr) 56px 64px 72px 100px 120px', bgcolor: 'action.hover', borderBottom: '2px solid', borderColor: 'divider' }}>
              {['Category', 'Item Name', 'Linked Product Category', 'Linked Product', 'Linked Variation', 'Qty', 'Orders', 'Status', 'Buyers', 'Action'].map((h) => (
                <Box key={h} sx={{ px: 2, py: 1.5, textAlign: h === 'Qty' || h === 'Orders' ? 'right' : h === 'Buyers' || h === 'Action' ? 'center' : 'left' }}>
                  <Typography variant="caption" fontWeight={700}>{h}</Typography>
                </Box>
              ))}
            </Box>
            {(rows || []).map((row, idx) => (
              <Box key={row.inventory_id} sx={{ minWidth: 1120, display: 'grid', gridTemplateColumns: 'minmax(100px,0.85fr) minmax(130px,1fr) minmax(140px,1fr) minmax(150px,1.1fr) minmax(150px,1.1fr) 56px 64px 72px 100px 120px', borderBottom: '1px solid', borderColor: 'divider', bgcolor: idx % 2 ? 'action.hover' : 'transparent' }}>
                <Box sx={{ p: 1.5 }}><Typography variant="body2" noWrap title={row.category_name || ''}>{row.category_name || '—'}</Typography></Box>
                <Box sx={{ p: 1.5 }}><Typography variant="body2" noWrap title={row.product_name || ''}>{row.product_name || '—'}</Typography></Box>
                <Box sx={{ p: 1.5 }}><Typography variant="body2" noWrap title={row.linked_product_category_names || ''}>{row.linked_product_category_names || '—'}</Typography></Box>
                <Box sx={{ p: 1.5 }}><Typography variant="body2" noWrap title={row.linked_product_name || ''}>{row.linked_product_name || '—'}</Typography></Box>
                <Box sx={{ p: 1.5 }}><Typography variant="body2" noWrap title={row.linked_variation_name || ''}>{row.linked_variation_name || '—'}</Typography></Box>
                <Box sx={{ p: 1.5, textAlign: 'right' }}><Typography variant="body2" fontWeight={600}>{Number(row.quantity || 0).toLocaleString()}</Typography></Box>
                <Box sx={{ p: 1.5, textAlign: 'right' }}><Typography variant="body2" color="text.secondary">—</Typography></Box>
                <Box sx={{ p: 1.5 }}><Typography variant="body2" noWrap>{row.status_name || '—'}</Typography></Box>
                <Box sx={{ p: 1.5, textAlign: 'center' }}>
                  <Tooltip title="Open manager">
                    <IconButton size="small" onClick={() => openManageDialog(row)}><PeopleIcon fontSize="small" /></IconButton>
                  </Tooltip>
                </Box>
                <Box sx={{ p: 1.5, display: 'flex', justifyContent: 'center' }}>
                  <Tooltip title="Manage mapping & buyers">
                    <IconButton size="small" onClick={() => openManageDialog(row)}><EditIcon fontSize="small" /></IconButton>
                  </Tooltip>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Paper>

      <Dialog open={editOpen} onClose={() => !saving && setEditOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6">Manage inventory item</Typography>
            <Typography variant="body2" color="text.secondary">
              Read-only item, product, and customer details from inventory, products, orders, and customers.
            </Typography>
            {saveSuccess && <Typography variant="body2" color="success.main">{saveSuccess}</Typography>}
            {saveError && <Typography variant="body2" color="error.main">{saveError}</Typography>}
          </Box>
          <IconButton onClick={() => !saving && setEditOpen(false)} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {editingItem && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1.25 }}>Item details</Typography>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={3}><TextField fullWidth size="small" label="Inventory ID" value={editingItem.inventory_id} InputProps={{ readOnly: true }} /></Grid>
                  <Grid item xs={12} sm={4}><TextField fullWidth size="small" label="Category" value={categories.find((c) => Number(c.category_id) === Number(editingItem.category_id))?.category_name || editingItem.category_id || '—'} InputProps={{ readOnly: true }} /></Grid>
                  <Grid item xs={12} sm={5}><TextField fullWidth size="small" label="Item name" value={editingItem.product_name ?? '—'} InputProps={{ readOnly: true }} /></Grid>
                  <Grid item xs={12} sm={3}><TextField fullWidth size="small" label="Item no" value={editingItem.product_no ?? '—'} InputProps={{ readOnly: true }} /></Grid>
                  <Grid item xs={12} sm={3}><TextField fullWidth size="small" label="Quantity" value={editingItem.quantity ?? 0} InputProps={{ readOnly: true }} /></Grid>
                  <Grid item xs={12} sm={3}><TextField fullWidth size="small" label="Status" value={statuses.find((s) => Number(s.status_id) === Number(editingItem.status))?.status_name || editingItem.status || '—'} InputProps={{ readOnly: true }} /></Grid>
                </Grid>
              </Box>

              <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1.25 }}>Product details</Typography>
                {productDetailsError && <Alert severity="error" sx={{ mb: 1.5 }}>{productDetailsError}</Alert>}
                {productDetailsLoading ? (
                  <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}><CircularProgress size={24} /></Box>
                ) : !editingItem.product_id ? (
                  <Alert severity="info">No product mapping found on this inventory item.</Alert>
                ) : (
                  <Grid container spacing={1.5}>
                    <Grid item xs={12} sm={3}><TextField fullWidth size="small" label="Product ID" value={editingItem.product_id || '—'} InputProps={{ readOnly: true }} /></Grid>
                    <Grid item xs={12} sm={3}><TextField fullWidth size="small" label="Variation ID" value={editingItem.variation_id || '—'} InputProps={{ readOnly: true }} /></Grid>
                    <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Product" value={productDetails?.product_name || selectedEditProduct?.name || `Product #${editingItem.product_id}`} InputProps={{ readOnly: true }} /></Grid>
                    <Grid item xs={12} sm={4}><TextField fullWidth size="small" label="SKU" value={productDetails?.variation?.sku || productDetails?.product?.sku || '—'} InputProps={{ readOnly: true }} /></Grid>
                    <Grid item xs={12} sm={4}><TextField fullWidth size="small" label="Price" value={currency(productDetails?.variation?.price_num ?? productDetails?.product?.price_num ?? 0)} InputProps={{ readOnly: true }} /></Grid>
                    <Grid item xs={12} sm={4}><TextField fullWidth size="small" label="Woo status" value={productDetails?.product?.status || '—'} InputProps={{ readOnly: true }} /></Grid>
                    <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Product categories" value={productDetails?.category_names || '—'} InputProps={{ readOnly: true }} /></Grid>
                    <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Variation" value={productDetails?.variation_name || '—'} InputProps={{ readOnly: true }} /></Grid>
                  </Grid>
                )}
              </Box>

              <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1.25 }}>Buyers from orders (unique users)</Typography>
                {!editingItem.product_id ? (
                  <Alert severity="info">Map a product first, then save. Buyers will load from orders automatically.</Alert>
                ) : (
                  <>
                    {buyersError && <Alert severity="error" sx={{ mb: 1.5 }}>{buyersError}</Alert>}
                    {buyersLoading ? (
                      <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}><CircularProgress size={24} /></Box>
                    ) : (
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Phone</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>Orders</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>Total Paid</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {buyers.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={5}>
                                <Typography variant="body2" color="text.secondary">No buyers found for this mapping yet.</Typography>
                              </TableCell>
                            </TableRow>
                          ) : (
                            buyers.map((b) => (
                              <TableRow key={`${b.contact_id || 'anon'}-${b.sponsor_name || ''}`}>
                                <TableCell>
                                  {b.contact_id ? (
                                    <Link to={`/devotees/${encodeURIComponent(b.contact_id)}`}>{b.sponsor_name || 'Unknown'}</Link>
                                  ) : (
                                    b.sponsor_name || 'Unknown'
                                  )}
                                </TableCell>
                                <TableCell>{b.customer_email || '—'}</TableCell>
                                <TableCell>{b.customer_phone || '—'}</TableCell>
                                <TableCell align="right">{Number(b.orders_count || 0).toLocaleString()}</TableCell>
                                <TableCell align="right">{currency(b.total_paid)}</TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    )}
                  </>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setEditOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
